import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    UntypedFormBuilder,
    ValidationErrors,
    ValidatorFn
} from '@angular/forms';
import { dateComparators, DateComparatorsType } from 'src/environments/date/date-comparators';
import { FormValidator } from 'src/app/validators/form-validator';
import { CustomFormControlAccessWithLoaders } from 'src/app/_core/tools/custom-form-control-access';
import { buildProviders, KalDate } from '@kalidea/kaligraphi';
import { tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

type DateWithOperatorFormGroup = {
    operator: FormControl<DateComparatorsType>,
    startDate: FormControl<KalDate>,
    endDate: FormControl<KalDate | null>,
};

export interface DateWithOperatorType {
    operator: DateComparatorsType
    startDate: KalDate;
    endDate: KalDate | null;
}

@Component({
    selector: 'app-date-with-operator',
    templateUrl: './date-with-operator.component.html',
    styleUrls: ['./date-with-operator.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(DateWithOperatorComponent),
    standalone: false
})
export class DateWithOperatorComponent extends CustomFormControlAccessWithLoaders implements OnInit {
    form: FormGroup<DateWithOperatorFormGroup>;

    readonly errorsMessages = {
        invaliddaterange: 'La date de fin doit être supérieure à la date de début.',
    };

    @Input()
    operatorOptionsList: { id: DateComparatorsType, label: string }[] =
        [
            {id: dateComparators.equal, label: 'Egale'},
            {id: dateComparators.lessThanEqual, label: 'En dessous de'},
            {id: dateComparators.moreThanEqual, label: 'A partir de'},
            {id: dateComparators.less, label: 'Moins de'},
            {id: dateComparators.more, label: 'Plus de'},
            {id: dateComparators.between, label: 'Entre'},
        ];

    @Input()
    betweenLabel = 'au';

    @Input()
    controlLabel = 'Date';

    @Input()
    startPlaceholder = 'A partir du';

    @Input()
    endPlaceholder = 'Jusqu\'au';

    constructor(protected cdr: ChangeDetectorRef,
                private fb: UntypedFormBuilder) {
        super(cdr);
    }

    get isBetweenOperator(): boolean {
        return this.form.value.operator === dateComparators.between;
    }

    get operatorControl(): AbstractControl {
        return this.form.get('operator');
    }

    get dateStartControl(): AbstractControl {
        return this.form.get('startDate');
    }

    get dateEndControl(): AbstractControl {
        return this.form.get('endDate');
    }

    validate(c: AbstractControl): Observable<ValidationErrors> {
        return of(this.form.invalid ? {dateoperatorerror: true} : null);
    }

    writeValue(value: DateWithOperatorType): void {
        let date = value || {} as DateWithOperatorType;
        if (!date.operator) {
            date.operator = dateComparators.lessThanEqual;
        }
        this.form.patchValue(value, {emitEvent: false});
        super.writeValue(value);
    }

    placeholder(startPlaceholder: string): string {
        const operator = this.form.value?.operator;
        return operator === dateComparators.between
            ? startPlaceholder
            : this.operatorOptionsList.find(o => o.id === operator)?.label || '';
    }

    setBetweenOperator(): void {
        this.operatorControl.patchValue(dateComparators.between);
    }

    private operatorValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const operatorList = this.operatorOptionsList.map(o => o.id);
            return operatorList.includes(control.value)
                ? null
                : {invalidValue: true};
        };
    }

    private initSubscriptions(): void {
        this.watch$ = this.form.valueChanges.pipe(
            tap(() => this.notifyUpdate(this.form.getRawValue()))
        );

        this.watch$ = this.operatorControl.valueChanges.pipe(
            tap(operator => {
                const [disableAction, validatorAction] = operator === dateComparators.between
                    ? ['enable', 'addValidators']
                    : ['disable', 'removeValidators'];

                this.dateEndControl[disableAction]({emitEvent: false});
                this.dateEndControl[validatorAction](FormValidator.isNotNull);
                if (operator === dateComparators.between) {
                    this.dateEndControl.patchValue(null, {emitEvent: false});
                }
                this.dateEndControl.updateValueAndValidity();
            })
        );
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            operator: this.fb.control(dateComparators.lessThanEqual, [FormValidator.isNotNull, this.operatorValidator()]),
            startDate: this.fb.control(new KalDate(), [FormValidator.date, FormValidator.isNotNull]),
            endDate: this.fb.control({value: null, disabled: true}, [FormValidator.date]),
        }, {
            validators: [
                form => {
                    if (form &&
                        (form.value.operator !== dateComparators.between
                            || form.get('startDate').invalid
                            || form.get('endDate').invalid)) {
                        return null;
                    }

                    const {endDate, startDate} = form.value || {};
                    return startDate?.isAfter?.(endDate) || endDate?.isBefore?.(startDate)
                        ? {invaliddaterange: true}
                        : null;
                }
            ]
        });

        this.initSubscriptions();
    }
}
